import { parseDay } from '@map-my-money/engine';
import type { Billing, Price, SignupCountry, UpgradeFeature } from '../api/types.ts';
import { LOCALE } from '../i18n/index.ts';
import { fullDate } from './format.ts';

const formatters = new Map<string, Intl.NumberFormat>();

/** "£6.99", "£49", "€7.99": pence only when the price has them. */
export function priceText(price: Pick<Price, 'amount' | 'currency'>, amount: string | number = price.amount): string {
  const value = typeof amount === 'number' ? amount : Number(amount);
  const whole = Math.abs(value - Math.round(value)) < 0.005;
  const key = `${price.currency}${whole}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, {
      style: 'currency', currency: price.currency.toUpperCase(), currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
    });
    formatters.set(key, f);
  }
  return f.format(value);
}

/** An ISO timestamp or date as "28 Sep 2026", on the local calendar day. */
export function billingDate(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return fullDate(parseDay(iso));
  const at = new Date(iso);
  const day = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
  return fullDate(parseDay(day));
}

/** Whole percent the annual price saves over twelve monthly payments. */
export function annualSaving(billing: { readonly prices: Pick<Billing['prices'], 'plus_monthly' | 'plus_annual'> }): number {
  const monthly = Number(billing.prices.plus_monthly.amount) * 12;
  const annual = Number(billing.prices.plus_annual.amount);
  return monthly > 0 ? Math.max(0, Math.round((1 - annual / monthly) * 100)) : 0;
}

export const UPGRADE_FEATURES: readonly UpgradeFeature[] = [
  'plans', 'shares', 'share_expiry', 'plan_people', 'editors', 'plan_read_only', 'editor_seat', 'branding', 'lapsed',
];
export const isUpgradeFeature = (value: string | null): value is UpgradeFeature =>
  value !== null && (UPGRADE_FEATURES as readonly string[]).includes(value);

/** Coach is shown only when the server offers it, with its limits and price. */
export const coachOffered = (billing: Pick<Billing, 'coach' | 'tiers' | 'prices'>): boolean =>
  billing.coach !== null && !!billing.tiers.coach && !!billing.prices.coach_annual;

/** A higher tier is on sale for this account (as the Worker's higherTier): otherwise a cap is just a cap. */
export const canUpgrade = (billing: Pick<Billing, 'enforced' | 'tier' | 'tiers'> | null | undefined): boolean =>
  !!billing?.enforced && (billing.tier === 'none' || (billing.tier === 'plus' && !!billing.tiers.coach));

export const isPaid = (billing: Pick<Billing, 'tier'> | null | undefined) => !!billing && billing.tier !== 'none';

/** `?country=uk|us`, as the /try playground links to pricing; anything else lets the server decide. */
export const signupCountry = (value: string | null): SignupCountry | null => (value === 'uk' || value === 'us' ? value : null);
